import type { KizenStepType } from 'pages/AutomationEngine/types';

import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import {
  mapApiSource,
  mapFlowSource,
} from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/variableDTO';
import type { VariableUpdateStep } from '../actionSectionTypes';
import type {
  ApiVariableSource,
  ArrayAggregationMode,
  ArrayValueResolution,
  VariableNameAndType,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { ArrayValueResolutionOptions } from 'pages/AutomationEngine/dialogs/VariableWizard/constants';

type APIObject = {
  variable: Omit<VariableNameAndType, 'id' | 'dataSubtype'> & {
    id: string;
    dataSubtype?: VariableNameAndType['dataSubtype'] & {
      id: string;
      description: string;
    };
  };
  isRequired: boolean;
  arrayAggregationMode: ArrayAggregationMode;
  deduplicateArray: boolean;
  valueResolution: ArrayValueResolution | null;
  sources: ApiVariableSource[];
};

const forFlow = (
  apiObject: APIObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary,
  stepType: KizenStepType
) => {
  const {
    variable,
    isRequired,
    arrayAggregationMode,
    deduplicateArray = false,
    valueResolution,
    sources,
  } = apiObject;

  return {
    isRequired,
    type: stepType,
    chosenVariable: {
      label: variable?.name,
      value: variable?.name.replaceAll(' ', '_'),
      meta: variable,
    },
    variable: {
      ...variable,
      dataSubtype: variable?.dataSubtype && {
        ...variable.dataSubtype,
        label: variable.dataSubtype.description,
        value: variable.dataSubtype.id,
      },
    },
    arrayAggregationMode,
    deduplicateArray,
    // existing steps saved before value resolution existed are null, which behaves as overwrite
    valueResolution: valueResolution || ArrayValueResolutionOptions.OVERWRITE,
    sources: sources.map(
      mapFlowSource(variable?.id, messageDictionary, addErrorMessage)
    ),
  };
};

const forApi = (step: VariableUpdateStep) => {
  const {
    variable,
    isRequired,
    arrayAggregationMode,
    deduplicateArray,
    valueResolution,
  } = step.details;

  const sources = (step.details.sources || []).map((s) =>
    mapApiSource(s, variable?.isArray)
  );

  const api = {
    ...step,
    details: {
      isRequired,
      variable: variable?.name,
      sources,
      arrayAggregationMode: arrayAggregationMode || 'add_all',
      deduplicateArray: deduplicateArray || false,
      // value resolution is only supported for array variables
      valueResolution: variable?.isArray
        ? valueResolution || ArrayValueResolutionOptions.ADD_ONLY
        : null,
    },
  };

  return api;
};

export const updateVariableDTO = { forApi, forFlow };
